import React, { useEffect, useState } from 'react'
import { CAlert, CButton, CCard, CCardBody, CCol, CImage, CRow, CFormSwitch } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilMinus, cilPlus } from '@coreui/icons'
import apiClient from '../../services/apiClient'
import { buildApiUrl } from '../../utils/getConfiguration'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

const WidgetSetAOI = ({
  label,
  img,
  aoiKey, // např. 'aoi1', 'aoi2', ...
  ageKey, // např. 'aoi1_age', 'aoi2_age', ...
  configEndpoint, // např. 'accounts_houseconfig_update.php'
  ageEndpoint, // např. 'accounts_houseage_update.php'
  aoiName, // např. 'House', 'Flat', 'Car', 'Health'
  onRefresh, // callback pro refresh rodiče (volitelné)
}) => {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()
  const [account, setAccount] = useState(null)

  const fetchAccount = async () => {
    try {
      const mail = localStorage.getItem('mail')
      const response = await apiClient.get(
        buildApiUrl('account', {
          email: mail,
        }),
      )
      setAccount(response.data[0] || null)
    } catch (error) {
      console.error('Error fetching account data', error)
    }
  }

  useEffect(() => {
    fetchAccount()
    // eslint-disable-next-line
  }, [])

  const setAOIStatus = async (status) => {
    if (!account) return
    const account_ID = localStorage.getItem('account_ID')
    // Klíč pro status je např. housestatus, flatstatus, carstatus, healthstatus
    const statusKey = `${label.toLowerCase()}status`
    await apiClient.get(
      buildApiUrl(configEndpoint, {
        [statusKey]: status,
        account_ID,
      }),
    )
    console.log('aoiName: ' + aoiName)
    if (status === 'Y') {
      localStorage.setItem(aoiName, 'ENABLED')
      await setAOIAge(currentYear)
    } else {
      localStorage.setItem(aoiName, 'DISABLED')
      await fetchAccount()
      onRefresh?.()
    }
  }

  const setAOIAge = async (age) => {
    if (!account) return
    const account_ID = localStorage.getItem('account_ID')
    await apiClient.get(
      buildApiUrl(ageEndpoint, {
        age,
        account_ID,
      }),
    )
    await fetchAccount()
    onRefresh?.()
  }

  const handleAgeChange = async (delta) => {
    if (!account) return
    let age = Number(account[ageKey]) || currentYear
    age += delta
    if (age < 1900) age = 1900
    if (age > currentYear) age = currentYear
    await setAOIAge(age)
  }

  if (!account) return null

  const rawStatus = account?.[aoiKey]
  const isEnabled = String(rawStatus) === 'Y'
  const isUnknown = rawStatus === null || String(rawStatus) === 'null'
  const statusSwitchId = `aoi-switch-${aoiName}`

  return (
    <CCard className="mb-3 border-0 shadow-sm">
      <CCardBody>
        <CRow className="align-items-center g-3">
          <CCol xs={12} md={4} lg={3}>
            <div className="d-flex align-items-center gap-3">
              <CImage
                rounded
                src={img}
                width={64}
                height={64}
                style={{ objectFit: 'cover' }}
                alt={t('widgetSetAoi.imageAlt', { label })}
              />
              <div>
                <div className="text-uppercase small text-muted">
                  {t('widgetSetAoi.areaOfInterest')}
                </div>
                <div className="fs-5 fw-semibold">{label}</div>
                <span
                  className={`badge bg-${isEnabled ? 'success' : 'secondary'} mt-1 text-uppercase`}
                >
                  {isEnabled ? t('widgetSetAoi.enabled') : t('widgetSetAoi.disabled')}
                </span>
              </div>
            </div>
          </CCol>
          <CCol xs={12} md="auto">
            <div className="d-flex align-items-center gap-3">
              <span className="text-uppercase small text-muted">{t('widgetSetAoi.status')}</span>
              <CFormSwitch
                size="xl"
                id={statusSwitchId}
                label=""
                aria-label={t('widgetSetAoi.statusAria', { label })}
                checked={isEnabled}
                onChange={() => setAOIStatus(isEnabled ? 'N' : 'Y')}
              />
              <label htmlFor={statusSwitchId} className="mb-0 fw-semibold">
                {isEnabled ? t('widgetSetAoi.active') : t('widgetSetAoi.inactive')}
              </label>
            </div>
          </CCol>
          {isUnknown ? (
            <CCol xs={12}>
              <CAlert color="info" className="mb-0">
                {t('widgetSetAoi.notAvailable', { label: label.toUpperCase() })}
              </CAlert>
            </CCol>
          ) : (
            isEnabled && (
              <CCol xs={12} md>
                <div className="d-flex flex-wrap align-items-center gap-3 justify-content-md-end">
                  <span className="text-uppercase small text-muted">
                    {t('widgetSetAoi.builtYear')}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <CButton
                      color="info"
                      variant="outline"
                      shape="rounded-pill"
                      onClick={() => handleAgeChange(-1)}
                    >
                      <CIcon icon={cilMinus} size="lg" />
                    </CButton>
                    <span
                      className="fs-5 fw-semibold"
                      style={{ minWidth: '3rem', textAlign: 'center' }}
                    >
                      {account[ageKey] ?? '--'}
                    </span>
                    <CButton
                      color="info"
                      variant="outline"
                      shape="rounded-pill"
                      onClick={() => handleAgeChange(1)}
                    >
                      <CIcon icon={cilPlus} size="lg" />
                    </CButton>
                  </div>
                </div>
              </CCol>
            )
          )}
        </CRow>
      </CCardBody>
    </CCard>
  )
}
WidgetSetAOI.propTypes = {
  label: PropTypes.string.isRequired,
  img: PropTypes.string.isRequired,
  aoiKey: PropTypes.string.isRequired,
  ageKey: PropTypes.string.isRequired,
  configEndpoint: PropTypes.string.isRequired,
  ageEndpoint: PropTypes.string.isRequired,
  aoiName: PropTypes.string.isRequired,
  onRefresh: PropTypes.func,
}

export default WidgetSetAOI
